import {DragDrop, DragRef, DropListRef, moveItemInArray} from '@angular/cdk/drag-drop';
import {Component, ElementRef, NgZone, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {
    DashboardFilter,
    DashboardSection,
    SupersetApiService,
    SupersetDashboardResponse,
    SupersetGuestTokenRequest,
} from '@npck/af-frontend-api';
import {RouterService, TAB} from '@npck/libs/commons';
import {DialogService, SnackbarService} from '@npck/libs/ui-kit';
import {EMPTY, forkJoin, mergeMap} from 'rxjs';
import {DeleteDialogComponent, DeleteDialogData} from '../../../components/delete-dialog/delete-dialog.component';
import {
    fieldExtractor,
    FilterDictionaryField,
    FilterEvent,
    FilterField,
    getFormattedDateOrNull,
} from '../../../components/full-table/filter/filter.component';
import {AuthHolderService} from '../../../domain/auth-holder.service';
import {DASHBOARD_ID, DASHBOARD_SECTION} from '../../../domain/query-params.keys';
import {Path} from '../../../path';
import {AnalyticsTab} from '../analytics.component';
import {sectionTitleKey, sectionToSlug, slugToSection} from '../dashboard-section';

const COMMON_DELETION_SCOPE = 'all';
const DRAG_CLASS = 'cdk-drag';
const GOV_ORGANIZATION_CODES = ['IO', 'GOV'];

@Component({
    selector: 'af-fraud-dashboards',
    templateUrl: './incidents-fraud-dashboards.component.html',
    styleUrl: './incidents-fraud-dashboards.component.scss',
    standalone: false,
})
export class IncidentsFraudDashboardsComponent implements OnInit, OnDestroy {
    dashboards: SupersetDashboardResponse[] = [];
    mountedDashboards: SupersetDashboardResponse[] = [];
    activeDashboards: SupersetDashboardResponse[] = [];
    supersetDomain: string;
    section: DashboardSection;
    titleKey: string;
    selectedTabIndex = 0;
    dashboardFilterFields: Record<string, FilterField[]> = {};
    visitedDashboardIds = new Set<string>();
    filterEvents: Record<string, FilterEvent> = {};
    filterValues: Record<string, SupersetGuestTokenRequest> = {};
    tabPathToTab: Record<string, AnalyticsTab> = {[Path.INCIDENTS]: 'tabIncidents'};
    private tabDropList: DropListRef;
    private tabDrags: DragRef[] = [];

    constructor(
        private routerService: RouterService,
        private activatedRoute: ActivatedRoute,
        private supersetApiService: SupersetApiService,
        private authHolderService: AuthHolderService,
        private dialogService: DialogService,
        private snackbarService: SnackbarService,
        private elementRef: ElementRef<HTMLElement>,
        private dragDrop: DragDrop,
        private ngZone: NgZone,
    ) {
    }

    get activeDashboard(): SupersetDashboardResponse {
        return this.dashboards[this.selectedTabIndex];
    }

    get canCreate(): boolean {
        return this.authHolderService.hasAccess(['npck-admin']);
    }

    get canEdit(): boolean {
        return this.authHolderService.hasAccess(['npck-admin']);
    }

    get canDelete(): boolean {
        return this.authHolderService.hasAccess(['npck-admin']);
    }

    ngOnInit(): void {
        this.section = slugToSection(this.activatedRoute.snapshot.paramMap.get(DASHBOARD_SECTION));
        this.titleKey = sectionTitleKey(this.section);
        forkJoin({
            config: this.supersetApiService.getSupersetConfig(),
            dashboards: this.supersetApiService.getDashboards(this.section),
        }).subscribe(({config, dashboards}) => {
            this.supersetDomain = config.supersetDomain;
            const activeTab = this.activatedRoute.snapshot.queryParamMap.get(TAB);
            const activeIndex = dashboards.items.findIndex((dashboard: { id: string; }) => dashboard.id === activeTab);
            this.selectedTabIndex = activeIndex < 0 ? 0 : activeIndex;
            this.setDashboards(dashboards.items);
        });
    }

    ngOnDestroy(): void {
        this.disposeTabDrag();
    }

    createDashboard(): void {
        this.routerService.navigate(this.sectionPath(Path.ANALYTICS_INCIDENTS_DASHBOARD_CREATE));
    }

    editDashboard(): void {
        this.routerService.navigate(
            this.sectionPath(Path.ANALYTICS_INCIDENTS_DASHBOARD_EDIT).replace(`:${DASHBOARD_ID}`, this.activeDashboard.id));
    }

    deleteDashboard(): void {
        const dashboard = this.activeDashboard;
        this.dialogService.openCustomDialogFitContent<DeleteDialogComponent, DeleteDialogResult>(
            DeleteDialogComponent,
            <DeleteDialogData>{
                id: dashboard.name,
                title: 'i18n_af_dashboardDeleteDialogTitle',
                subtitle: 'i18n_af_dashboardDeleteDialogSubtitle',
                showReason: true,
                reasonFilter: [COMMON_DELETION_SCOPE],
            })
            .pipe(mergeMap(result => !result?.reason && EMPTY
                || this.supersetApiService.deleteDashboard(dashboard.id, {
                    deletionReason: result.reason,
                    comment: result.comment,
                })))
            .subscribe(() => {
                this.snackbarService.showSnackBar('i18n_af_snackbarMessageDashboardDeleted', 'success', false, {name: dashboard.name});
                this.reloadDashboards();
            });
    }

    handleNavigation($event: number): void {
        this.selectedTabIndex = $event;
        this.syncActiveDashboard();
        this.routerService.navigate(
            this.sectionPath(Path.ANALYTICS_INCIDENTS_DASHBOARDS),
            {
                queryParams: {[TAB]: this.activeDashboard?.id},
                queryParamsHandling: 'merge',
            });
    }

    handleFilterChange(dashboard: SupersetDashboardResponse, filterEvent: FilterEvent): void {
        this.filterEvents = {...this.filterEvents, [dashboard.id]: filterEvent};
        this.filterValues = {
            ...this.filterValues,
            [dashboard.id]: {
                id: dashboard.id,
                reportDate: getFormattedDateOrNull(filterEvent.reportDate?.date),
                organizationBins: fieldExtractor(filterEvent.sourceOrganizations),
                governmentBins: fieldExtractor(filterEvent.incidentGovernment),
            },
        };
    }

    private sectionPath(path: Path): string {
        return path.replace(`:${DASHBOARD_SECTION}`, sectionToSlug(this.section));
    }

    private moveDashboard(previousIndex: number, currentIndex: number): void {
        if (previousIndex === currentIndex) {
            return;
        }
        const reordered = [...this.dashboards];
        moveItemInArray(reordered, previousIndex, currentIndex);
        this.setDashboards(reordered);
        this.supersetApiService.reorderDashboards({
            section: this.section,
            dashboardIds: reordered.map(dashboard => dashboard.id),
        }).subscribe(() => this.reloadDashboards());
    }

    private setupTabDrag(): void {
        this.disposeTabDrag();
        if (!this.canEdit || this.dashboards.length < 2) {
            return;
        }
        const labelsContainer = this.elementRef.nativeElement.querySelector<HTMLElement>('.mat-mdc-tab-labels');
        const labels = Array.from(labelsContainer?.querySelectorAll<HTMLElement>('.mat-mdc-tab') ?? []);
        if (labels.length !== this.dashboards.length) {
            return;
        }
        this.tabDrags = labels.map(label => {
            label.classList.add(DRAG_CLASS);
            return this.dragDrop.createDrag(label);
        });
        this.tabDropList = this.dragDrop.createDropList(labelsContainer)
            .withOrientation('horizontal')
            .withItems(this.tabDrags);
        this.tabDropList.dropped.subscribe(event =>
            this.ngZone.run(() => this.moveDashboard(event.previousIndex, event.currentIndex)));
    }

    private disposeTabDrag(): void {
        this.tabDropList?.dispose();
        this.tabDropList = null;
        this.tabDrags.forEach(drag => {
            drag.getRootElement()?.classList.remove(DRAG_CLASS);
            drag.dispose();
        });
        this.tabDrags = [];
    }

    private createFilterFields(dashboard: SupersetDashboardResponse): FilterField[] {
        const fields: FilterField[] = [];
        if (DashboardFilter.REPORT_DATE in dashboard.filters) {
            fields.push({name: 'reportDate', type: 'date'});
        }
        if (DashboardFilter.ORGANIZATION in dashboard.filters) {
            fields.push(<FilterDictionaryField>{
                name: 'sourceOrganizations',
                type: 'select',
                isMultiSelect: true,
                dictionaryName: 'bin',
                values: [],
            });
        }
        if (DashboardFilter.GOV_ORGANIZATION in dashboard.filters) {
            fields.push(<FilterDictionaryField>{
                name: 'incidentGovernment',
                type: 'select',
                isMultiSelect: true,
                dictionaryName: 'bin',
                filterBy: GOV_ORGANIZATION_CODES,
                values: [],
            });
        }
        return fields;
    }

    private reloadDashboards(): void {
        this.supersetApiService.getDashboards(this.section)
            .subscribe((dashboards: { items: SupersetDashboardResponse[]; }) => this.setDashboards(dashboards.items));
    }

    private setDashboards(dashboards: SupersetDashboardResponse[]): void {
        const activeId = this.activeDashboard?.id;
        this.dashboards = dashboards;
        if (activeId) {
            const activeIndex = dashboards.findIndex(dashboard => dashboard.id === activeId);
            this.selectedTabIndex = activeIndex < 0 ? 0 : activeIndex;
        }
        this.mountedDashboards = [...dashboards].sort((first, second) => first.id.localeCompare(second.id));
        dashboards.forEach(dashboard => {
            this.filterValues[dashboard.id] ??= {id: dashboard.id};
            this.dashboardFilterFields[dashboard.id] = this.createFilterFields(dashboard);
        });
        this.syncActiveDashboard();
        setTimeout(() => this.setupTabDrag());
    }

    private syncActiveDashboard(): void {
        const dashboard = this.activeDashboard;
        this.activeDashboards = dashboard ? [dashboard] : [];
        if (dashboard) {
            this.visitedDashboardIds.add(dashboard.id);
        }
    }
}

interface DeleteDialogResult {
    reason: string;
    comment: string;
}
